import { useState } from "react";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import {
  useAiCostsCurrent,
  useAiCostsHistory,
  useAiCostsJobs,
  useUpdateBudget,
  useSetBudgetOverride,
} from "@/hooks/use-ai-costs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export function BrandSettingsAiUsagePage() {
  const { t } = useTranslation("settings");
  const locale = useLocale();
  const { brand } = useBrandContext();
  const { canViewAiCosts, canEditBudget, canOverrideBudget } = usePermissions();
  const brandId = brand?.id ?? "";

  const { data: current } = useAiCostsCurrent(brandId);
  const { data: history } = useAiCostsHistory(brandId, 30);
  const { data: jobsData } = useAiCostsJobs(brandId, 50);
  const updateBudget = useUpdateBudget(brandId);
  const setOverride = useSetBudgetOverride(brandId);

  const [capInput, setCapInput] = useState<string>("");
  const [overrideDate, setOverrideDate] = useState<string>("");

  if (!canViewAiCosts) return <div className="p-6">{t("aiUsage.notAuthorized")}</div>;
  if (!current) return <div className="p-6">{t("aiUsage.loading")}</div>;

  const spent = Number(current.spent);
  const cap = current.cap ? Number(current.cap) : null;

  return (
    <div className="px-6 py-8 w-full space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("aiUsage.title")}</h1>
        {current.overrideActive && (
          <div className="mt-2 rounded bg-yellow-100 p-2 text-sm text-yellow-900">
            {t("aiUsage.budgetOverride", { date: formatDate(new Date(current.overrideUntil!), locale, { dateStyle: "medium" }) })}
          </div>
        )}
      </header>

      <section className="rounded-lg border p-4">
        <div className="text-sm text-muted-foreground">{t("aiUsage.thisMonth")}</div>
        <div className="text-3xl font-semibold">
          ${spent.toFixed(2)}
          {cap !== null && (
            <span className="text-muted-foreground text-base">
              {" "}
              / ${cap.toFixed(2)}
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {cap !== null
            ? t("aiUsage.percentUsed", { percent: (current.percentUsed * 100).toFixed(1) })
            : t("aiUsage.noCap")}
        </div>
      </section>

      <section className="rounded-lg border p-4">
        <h2 className="mb-2 text-sm font-medium">{t("aiUsage.dailySpend")}</h2>
        {history && history.daily.length > 0 ? (
          <ResponsiveContainer width="100%" height={160}>
            <LineChart
              data={history.daily.map((d) => ({
                date: d.date,
                cost: Number(d.cost),
              }))}
            >
              <XAxis dataKey="date" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip />
              <Line type="monotone" dataKey="cost" stroke="#10b981" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-sm text-muted-foreground">{t("aiUsage.noUsage")}</div>
        )}
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border p-4">
          <h3 className="mb-2 text-sm font-medium">{t("aiUsage.byModel")}</h3>
          <table className="w-full text-sm">
            <tbody>
              {current.breakdownByModel.map((m) => (
                <tr key={m.model} className="border-t">
                  <td className="py-1">{m.model}</td>
                  <td className="py-1 text-right">
                    ${Number(m.cost).toFixed(2)}
                  </td>
                  <td className="py-1 text-right text-muted-foreground">
                    {t("aiUsage.calls", { count: m.calls })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="rounded-lg border p-4">
          <h3 className="mb-2 text-sm font-medium">{t("aiUsage.byJobType")}</h3>
          <table className="w-full text-sm">
            <tbody>
              {current.breakdownByJobType.map((j) => (
                <tr key={j.jobType} className="border-t">
                  <td className="py-1">{j.jobType}</td>
                  <td className="py-1 text-right">
                    ${Number(j.cost).toFixed(2)}
                  </td>
                  <td className="py-1 text-right text-muted-foreground">
                    {t("aiUsage.calls", { count: j.calls })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {canEditBudget && (
        <section className="rounded-lg border p-4">
          <h3 className="mb-2 text-sm font-medium">{t("aiUsage.monthlyCap")}</h3>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder={cap?.toString() ?? t("aiUsage.unlimited")}
              className="rounded border px-2 py-1 text-sm"
              value={capInput}
              onChange={(e) => setCapInput(e.target.value)}
            />
            <button
              className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
              onClick={() =>
                updateBudget.mutate(capInput === "" ? null : Number(capInput))
              }
              disabled={updateBudget.isPending}
            >
              {t("actions.save", { ns: "common" })}
            </button>
            <button
              className="rounded border px-3 py-1 text-sm"
              onClick={() => updateBudget.mutate(null)}
            >
              {t("aiUsage.clearCap")}
            </button>
          </div>
        </section>
      )}

      {canOverrideBudget && (
        <section className="rounded-lg border p-4">
          <h3 className="mb-2 text-sm font-medium">{t("aiUsage.raiseCap")}</h3>
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="rounded border px-2 py-1 text-sm"
              value={overrideDate}
              onChange={(e) => setOverrideDate(e.target.value)}
            />
            <button
              className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
              onClick={() =>
                setOverride.mutate(
                  // End of the selected day in the viewer's local timezone
                  // (no trailing "Z"), then normalized to a UTC instant.
                  new Date(overrideDate + "T23:59:59").toISOString(),
                )
              }
              disabled={!overrideDate || setOverride.isPending}
            >
              {t("aiUsage.applyOverride")}
            </button>
          </div>
        </section>
      )}

      <section className="rounded-lg border p-4">
        <h3 className="mb-2 text-sm font-medium">{t("aiUsage.recentJobs")}</h3>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-1">{t("aiUsage.time")}</th>
                <th className="py-1">{t("aiUsage.model")}</th>
                <th className="py-1">{t("aiUsage.job")}</th>
                <th className="py-1 text-right">{t("aiUsage.tokensUnits")}</th>
                <th className="py-1 text-right">{t("aiUsage.cost")}</th>
              </tr>
            </thead>
            <tbody>
              {jobsData?.jobs?.map((j: any) => (
                <tr key={j.id} className="border-t">
                  <td className="py-1">
                    {formatDate(new Date(j.createdAt), locale, { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className="py-1">{j.model}</td>
                  <td className="py-1">{j.jobType}</td>
                  <td className="py-1 text-right">
                    {BigInt(j.inputTokens) > BigInt(0)
                      ? `${j.inputTokens}/${j.outputTokens}`
                      : `${j.unitsCount}u`}
                  </td>
                  <td className="py-1 text-right">
                    ${Number(j.costUsd).toFixed(4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
